// SCR-RND-04 / SCR-SAL-04 Round (frozen mockups-rnd-v1, mockups-sal-v1): context strip, stage bar, cash held by the
// Seller, actions by stage and tabs Invoices · Unpaid · Stock (the truck's movements under its tiles); P7 adds Cash.
// A Seller sees their own truck; the owner picks any truck in the context strip (?truck=).
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Badge,
  ContextStrip,
  EmptyState,
  Field,
  Input,
  List,
  ListRow,
  Notice,
  QuickActions,
  Select,
  SkeletonList,
  StageBar,
  StockTile,
  SummaryCard,
  Tabs,
  Tiles,
  type QuickAction,
} from '../../design/kit';
import { ROUNDS, loadLiveRound, loadTruckStock } from '../../data/rounds';
import { loadMovements } from '../../data/stock';
import { SALES, isPendingId, loadRoundSales, loadSales, loadUnpaid, type SaleLineInput } from '../../data/sales';
import { peekResume, takeResume } from '../../data/offline';
import { Code, Name } from '../../i18n/Isolate';
import type { Language } from '../../i18n';
import { businessToday, formatDate, formatDateTime, formatMoney } from '../../lib/format';
import { useSession } from '../../app/session';
import { useTab } from '../catalog/common';
import { STOCK, useStockWords } from '../stock/StockSheets';
import { useInventory } from '../stock/InventoryPage';
import { EndSellingSheet, MoveSheet, OpenRoundSheet, ReopenSheet } from './RoundSheets';
import { useTrucks } from './TrucksPage';
import { InvoiceRows } from '../sales/SalesPage';
import { CollectSheet, SaleSheet } from '../sales/SaleSheets';
import { RoundCloseSheet } from './RoundClose';
import { EXPENSES, loadRoundExpenses, loadTruckRoundCloses } from '../../data/expenses';

const TABS = ['invoices', 'unpaid', 'truckstock', 'cash'] as const;
type Tab = (typeof TABS)[number];
type Sheet = 'open' | 'move' | 'unload' | 'end' | 'reopen' | 'close' | 'sale' | 'collect' | null;

export function RoundPage() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language as Language;
  const { role, userId } = useSession();
  const owner = role === 'owner';
  const words = useStockWords();
  const [params, setParams] = useSearchParams();
  const [tab, setTab] = useTab(TABS, 'invoices');
  const navigate = useNavigate();
  const [collectFor, setCollectFor] = useState<string>();
  const [sheet, setSheet] = useState<Sheet>(() => (peekResume() ? 'sale' : null));
  // A refused sale taken back for editing reopens the sale sheet with its lines (ADR-128).
  const [resume] = useState(() => takeResume());
  const trucks = useTrucks();
  const inventory = useInventory();
  const mine = (trucks.data ?? []).filter((k) => owner || k.seller_id === userId);
  const truck = mine.find((k) => k.id === params.get('truck')) ?? mine.find((k) => k.round_id) ?? mine[0];
  const round = useQuery({
    queryKey: [...ROUNDS, 'live', truck?.id],
    queryFn: () => loadLiveRound(truck?.id ?? ''),
    enabled: Boolean(truck),
  });
  const closedRounds = useQuery({
    queryKey: [...EXPENSES, 'closed-rounds', truck?.id],
    queryFn: () => loadTruckRoundCloses(truck?.id ?? ''),
    enabled: Boolean(truck) && round.isSuccess && !round.data,
  });
  const stock = useQuery({
    queryKey: [...ROUNDS, 'stock', truck?.id],
    queryFn: () => loadTruckStock(truck?.id ?? ''),
    enabled: Boolean(truck),
  });
  const moves = useQuery({
    queryKey: [...STOCK, 'movements', 'truck', truck?.id],
    queryFn: () => loadMovements(200),
    enabled: Boolean(truck) && tab === 'truckstock',
  });
  const rid = round.data?.id;
  const sales = useQuery({
    queryKey: [...SALES, 'round', rid],
    queryFn: () => loadSales({ round: rid }),
    enabled: Boolean(rid) && tab === 'invoices',
  });
  const unpaid = useQuery({
    queryKey: [...SALES, 'unpaid', round.data?.seller_id],
    queryFn: () => loadUnpaid(round.data?.seller_id ?? ''),
    enabled: Boolean(rid) && tab === 'unpaid',
  });
  const summary = useQuery({
    queryKey: [...SALES, 'roundsum', rid],
    queryFn: () => loadRoundSales(rid ?? ''),
    enabled: Boolean(rid),
  });
  const cashSales = useQuery({
    queryKey: [...SALES, 'paid-round', rid],
    queryFn: () => loadSales({ paidRound: rid }),
    enabled: Boolean(rid) && tab === 'cash',
  });
  const cashExpenses = useQuery({
    queryKey: [...EXPENSES, 'round', rid],
    queryFn: () => loadRoundExpenses(rid ?? ''),
    enabled: Boolean(rid) && tab === 'cash',
  });

  if (trucks.isError) return <Notice tone="danger">{t('err.generic')}</Notice>;
  if (!trucks.data) return <SkeletonList label={t('loading')} />;
  if (!truck) return <EmptyState icon="truck" title={t('rd.notruck')} text={t('rd.notruck.text')} />;
  const r = round.data ?? null;
  const stage = r?.status === 'selling_ended' ? 1 : 0;
  const pickTruck = (id: string) =>
    setParams(
      (p) => {
        p.set('truck', id);
        return p;
      },
      { replace: true },
    );
  const actions: QuickAction[] = !r
    ? []
    : r.status === 'open'
      ? [
          ...(r.seller_id === userId && truck.ownership !== 'external'
            ? [
                {
                  id: 'sale',
                  label: t('act.newsale'),
                  icon: 'plus' as const,
                  main: true,
                  onSelect: () => setSheet('sale'),
                },
                { id: 'collect', label: t('act.collect'), icon: 'cash' as const, onSelect: () => setSheet('collect') },
              ]
            : []),
          {
            id: 'move',
            label: t('act.move'),
            icon: 'swap',
            main: r.seller_id !== userId || truck.ownership === 'external',
            onSelect: () => setSheet('move'),
          },
          { id: 'end', label: t('act.endselling'), icon: 'check', onSelect: () => setSheet('end') },
        ]
      : [
          ...(owner
            ? [
                {
                  id: 'close',
                  label: t('p7.closeRound'),
                  icon: 'check' as const,
                  main: true,
                  onSelect: () => setSheet('close'),
                },
              ]
            : []),
          ...(owner
            ? [
                {
                  id: 'reopen',
                  label: t('act.reopen'),
                  icon: 'return' as const,
                  onSelect: () => setSheet('reopen'),
                },
              ]
            : []),
          {
            id: 'unload',
            label: t('act.returnstock'),
            icon: 'layers',
            main: !owner,
            onSelect: () => setSheet('unload'),
          },
        ];
  const onTruck = (stock.data ?? []).filter((x) => x.calc_pieces > 0);
  const truckMoves = (moves.data ?? []).filter((m) => m.lines.some((l) => l.truck_id === truck.id));

  return (
    <>
      <ContextStrip
        truck={
          <Field label={t('truck')}>
            {({ id }) =>
              owner ? (
                <Select id={id} value={truck.id} onChange={(e) => pickTruck(e.target.value)}>
                  {mine.map((k) => (
                    <option key={k.id} value={k.id}>
                      {k.code}
                    </option>
                  ))}
                </Select>
              ) : (
                <Input id={id} dir="ltr" readOnly value={truck.code} />
              )
            }
          </Field>
        }
        round={
          <Field label={t('tk.seller')}>
            {({ id }) => <Input id={id} dir="auto" readOnly value={truck.seller_name ?? ''} />}
          </Field>
        }
        workingDate={
          <Field label={t('workdate')}>
            {({ id }) => <Input id={id} dir="ltr" readOnly value={formatDate(businessToday(), lang)} />}
          </Field>
        }
      />
      {truck.ownership === 'external' && <Notice tone="info">{t('rd.external')}</Notice>}
      {owner && truck.seller_id === userId && (
        <div>
          <Badge tone="primary">{t('own.badge')}</Badge>
        </div>
      )}
      {!round.data && round.isLoading ? (
        <SkeletonList rows={1} label={t('loading')} />
      ) : !r ? (
        <>
          <EmptyState
            icon="route"
            title={t('rd.none')}
            text={t('rd.none.text')}
            action={
              truck.seller_id === userId ? (
                <button type="button" className="yv-btn yv-btn--main" onClick={() => setSheet('open')}>
                  {t('act.openround')}
                </button>
              ) : undefined
            }
          />
          {closedRounds.data && closedRounds.data.length > 0 && (
            <List label={t('p7.tab.rounds')}>
              {closedRounds.data.map((item) => (
                <ListRow
                  key={item.round_id}
                  lead={{ icon: 'route' }}
                  title={<Code>{item.number}</Code>}
                  sub={`${formatDateTime(item.closed_at, lang)} · ${item.truck_code}`}
                  value={formatMoney(item.handed_over, lang)}
                  onOpen={() => navigate(`/round/closed/${item.round_id}`)}
                />
              ))}
            </List>
          )}
        </>
      ) : (
        <>
          {owner && truck.seller_id === userId && <Notice tone="info">{t('own.round.note')}</Notice>}
          <StageBar label={t('rd.no')} steps={[t('st.open'), t('st.ended'), t('st.closedr')]} current={stage} />
          <SummaryCard
            label={t('rd.cash')}
            value={r.cash !== null ? formatMoney(r.cash, lang) : '—'}
            sub={
              <>
                <Code>{r.number}</Code> · {t('rd.started')} {formatDateTime(r.started_at, lang)} · {t('rd.invoices')}{' '}
                {summary.data?.invoices ?? 0}
              </>
            }
            figures={[
              { label: t('rd.paidinv'), value: String(summary.data?.paid_invoices ?? 0) },
              { label: t('rd.unpaidmade'), value: formatMoney(summary.data?.unpaid_made ?? 0, lang) },
            ]}
          />
          {r.status === 'selling_ended' && !owner && <Notice tone="warning">{t('rd.awaits')}</Notice>}
          <QuickActions actions={actions} />
          <Tabs
            label={r.number}
            value={tab}
            onChange={(v) => setTab(v as Tab)}
            items={TABS.map((id) => ({ id, label: t(`tab.${id}`) }))}
          />
          {tab === 'invoices' ? (
            !sales.data ? (
              <SkeletonList label={t('loading')} />
            ) : sales.data.length === 0 ? (
              <EmptyState icon="file" title={t('empty.invoices')} text={t('empty.invoices.text')} />
            ) : (
              <InvoiceRows sales={sales.data} />
            )
          ) : tab === 'unpaid' ? (
            !unpaid.data ? (
              <SkeletonList label={t('loading')} />
            ) : unpaid.data.length === 0 ? (
              <EmptyState icon="cash" title={t('empty.unpaid')} text={t('col.pick')} />
            ) : (
              <List label={t('tab.unpaid')}>
                {unpaid.data.map((s) => (
                  <ListRow
                    key={s.id}
                    testId={`unpaid-${s.number}`}
                    lead={{ icon: 'cash' }}
                    title={<Name>{s.customer}</Name>}
                    sub={
                      <>
                        <Code>{s.number}</Code> · {formatDate(s.business_date, lang)}
                      </>
                    }
                    value={formatMoney(s.total, lang)}
                    actions={
                      r.status === 'open' && r.seller_id === userId && truck.ownership !== 'external' ? (
                        <button
                          type="button"
                          className="yv-btn yv-btn--pill"
                          onClick={() => {
                            setCollectFor(s.customer_id);
                            setSheet('collect');
                          }}
                        >
                          {t('act.collect')}
                        </button>
                      ) : undefined
                    }
                  />
                ))}
              </List>
            )
          ) : tab === 'cash' ? (
            !cashSales.data || !cashExpenses.data ? (
              <SkeletonList label={t('loading')} />
            ) : (
              <>
                <SummaryCard
                  label={t('rd.cash')}
                  value={formatMoney(r.cash ?? 0, lang)}
                  figures={[
                    { label: t('p7.close.paid'), value: formatMoney(summary.data?.paid_total ?? 0, lang) },
                    {
                      label: t('p7.expensesWage'),
                      value: formatMoney(
                        cashExpenses.data.reduce(
                          (sum, item) => sum + (item.correction ? -item.amount : item.amount),
                          0,
                        ),
                        lang,
                      ),
                    },
                  ]}
                />
                <List label={t('p7.close.paid')}>
                  {cashSales.data.map((sale) => (
                    <ListRow
                      key={sale.id}
                      lead={{ icon: 'cash' }}
                      title={
                        <>
                          <Code>{sale.number}</Code> · <Name>{sale.customer}</Name>
                        </>
                      }
                      sub={formatDate(sale.business_date, lang)}
                      value={formatMoney(sale.total, lang)}
                    />
                  ))}
                  {cashSales.data.length === 0 && <ListRow lead={{ icon: 'cash' }} title={t('p7.none')} />}
                </List>
                <List label={t('p7.expensesWage')}>
                  {cashExpenses.data.map((expense) => (
                    <ListRow
                      key={expense.id}
                      lead={{ icon: 'file' }}
                      title={expense.category}
                      sub={
                        <>
                          {expense.number} · {formatDate(expense.business_date, lang)}
                        </>
                      }
                      value={formatMoney(expense.correction ? -expense.amount : expense.amount, lang)}
                    />
                  ))}
                  {cashExpenses.data.length === 0 && <ListRow lead={{ icon: 'file' }} title={t('p7.none')} />}
                </List>
              </>
            )
          ) : !stock.data ? (
            <SkeletonList label={t('loading')} />
          ) : (
            <>
              {onTruck.length === 0 ? (
                <EmptyState icon="layers" title={t('empty.truckstock')} text={t('empty.truckstock.text')} />
              ) : (
                <Tiles label={t('tab.truckstock')}>
                  {onTruck.map((x) => (
                    <StockTile
                      key={x.product_id}
                      testId={`ttile-${x.sku ?? x.product_id}`}
                      name={<Name>{x.name}</Name>}
                      quantity={words.qty(x, x.boxes, x.loose)}
                      sub={`${t('calc.pieces', { n: x.calc_pieces })}${x.box_capacity !== null ? ` · ${t('boxof', { n: x.box_capacity })}` : ''}`}
                    />
                  ))}
                </Tiles>
              )}
              <List label={t('tab.truckmoves')}>
                {truckMoves.map((m) => (
                  <ListRow
                    key={m.id}
                    testId={`tmove-${m.id}`}
                    lead={{ icon: 'swap' }}
                    title={t(
                      m.kind === 'load'
                        ? 'mv.dir.load'
                        : m.kind === 'unload'
                          ? 'mv.dir.unload'
                          : m.kind === 'sale'
                            ? 'move.sale'
                            : m.kind === 'box_open'
                              ? 'move.boxopen'
                              : 'tc.title',
                    )}
                    sub={
                      <>
                        {formatDateTime(m.at, lang)}
                        {m.actor_name && (
                          <>
                            {' · '}
                            <Name>{m.actor_name}</Name>
                          </>
                        )}
                        {m.lines
                          .filter((l) => l.truck_id === truck.id)
                          .map((l) => (
                            <span key={l.product_id}>
                              <br />
                              <Name>{l.product}</Name> {words.signed(l, l.boxes, l.loose)}
                            </span>
                          ))}
                      </>
                    }
                  />
                ))}
              </List>
            </>
          )}
        </>
      )}
      {sheet === 'sale' && r && (
        <SaleSheet
          round={r.id}
          seller={r.seller_id}
          stock={stock.data ?? []}
          onClose={() => setSheet(null)}
          {...(resume ? { initial: { customer: resume.customer, lines: resume.lines as SaleLineInput[] } } : {})}
          onDone={(id) => navigate(isPendingId(id) ? '/round/queue' : `/sales/${id}`)}
        />
      )}
      {sheet === 'collect' && r && (
        <CollectSheet
          round={r.id}
          seller={r.seller_id}
          {...(collectFor ? { initialCustomer: collectFor } : {})}
          onClose={() => {
            setSheet(null);
            setCollectFor(undefined);
          }}
        />
      )}
      {sheet === 'open' && <OpenRoundSheet truck={truck} onClose={() => setSheet(null)} />}
      {sheet === 'end' && r && <EndSellingSheet round={r} onClose={() => setSheet(null)} />}
      {sheet === 'reopen' && r && <ReopenSheet round={r} onClose={() => setSheet(null)} />}
      {sheet === 'close' && r && owner && (
        <RoundCloseSheet
          roundId={r.id}
          roundNumber={r.number}
          cash={Number(r.cash ?? 0)}
          own={r.seller_id === userId}
          onClose={() => setSheet(null)}
        />
      )}
      {(sheet === 'move' || sheet === 'unload') && (
        <MoveSheet
          trucks={[truck]}
          initialTruck={truck.id}
          initialDirection={sheet === 'unload' ? 'unload' : 'load'}
          garage={(inventory.data ?? []).filter((x) => x.location === 'garage')}
          truckStock={() => stock.data ?? []}
          onClose={() => setSheet(null)}
        />
      )}
    </>
  );
}
